import React, { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  VideoFile as VideoIcon,
  DownloadForOffline as DownloadIcon
} from '@mui/icons-material';
import { SERVER_BASE_URL } from '../config';
import { supabase } from '../lib/supabase';

interface Shot {
  id: number;
  startTime: number;  // Now absolute time in full video
  endTime: number;    // Now absolute time in full video
  duration: number;
  startFrame?: number;
  endFrame?: number;
  isEdited: boolean;
  useBlurredBackground?: boolean; // New option for blurred pillarbox effect
  originalFraming: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null;
  customFraming: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null;
  thumbnailUrl: string | null;
}

interface ShotData {
  videoId: string;
  originalVideoUrl: string;
  inPoint: number;
  duration: number;
  editStart: number;
  editEnd: number;
  editDuration: number;
  fullVideoDuration: number;
  fullVideoPath: string;
  frameRate?: number;
  shots: Shot[];
  createdAt?: string;
}

interface VideoFramingEditorProps {
  open: boolean;
  onClose: () => void;
  videoUrl: string;
  horizontalUrl?: string;
  inPoint: number;
  duration: number;
  onVideoGenerated?: (videoUrl: string) => void;
}

interface CropBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

const VideoFramingEditor: React.FC<VideoFramingEditorProps> = ({
  open,
  onClose,
  videoUrl,
  horizontalUrl,
  inPoint,
  duration,
  onVideoGenerated
}) => {
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shotData, setShotData] = useState<ShotData | null>(null);
  const [currentShotIndex, setCurrentShotIndex] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [reassembling, setReassembling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cropBox, setCropBox] = useState<CropBox>({ x: 656, y: 0, width: 608, height: 1088 });
  const [originalCropBox, setOriginalCropBox] = useState<CropBox>({ x: 656, y: 0, width: 608, height: 1088 });
  const [boundaryMonitor, setBoundaryMonitor] = useState<number | null>(null);
  
  // Progress tracking
  const [progressStep, setProgressStep] = useState<string>('');
  const [progressPercent, setProgressPercent] = useState<number>(0);
  const [progressMessage, setProgressMessage] = useState<string>('');

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cropContainerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const dragElement = useRef<'move' | 'resize' | null>(null);
  const pendingOperation = useRef<Promise<void> | null>(null);
  const isTogglingPlayback = useRef(false);
  const socketRef = useRef<Socket | null>(null);

  // Set up WebSocket connection for progress updates
  useEffect(() => {
    let cancelled = false;
    if (open) {
      (async () => {
        const { data } = await supabase.auth.getSession();
        if (cancelled) return;
        const token = data.session?.access_token;
        console.log('[WebSocket] Connecting to server...');
        socketRef.current = io(process.env.REACT_APP_SERVER_URL || 'http://localhost:3001', {
          auth: token ? { token } : undefined,
        });

        socketRef.current.on('connect', () => {
          console.log('[WebSocket] Connected to server');
        });

        socketRef.current.on('videoProgress', (data) => {
          console.log('[WebSocket] Progress update:', data);
          setProgressStep(data.step);
          setProgressPercent(parseFloat(data.progress) || 0);
          setProgressMessage(data.message);

          // Reset progress after completion
          if (data.step === 'complete') {
            setTimeout(() => {
              setReassembling(false);
              setProgressStep('');
              setProgressPercent(0);
              setProgressMessage('');
            }, 2000); // Show complete message for 2 seconds
          }
        });

        socketRef.current.on('disconnect', () => {
          console.log('[WebSocket] Disconnected from server');
        });
      })();
    }

    return () => {
      cancelled = true;
      if (socketRef.current) {
        console.log('[WebSocket] Cleaning up connection');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [open]);

  // Initialize shot detection when dialog opens - always run fresh detection
  useEffect(() => {
    if (open && videoUrl) {
      // Clear any existing shot data first
      setShotData(null);
      detectShots();
    }
  }, [open, videoUrl]);

  // Cleanup when dialog closes
  useEffect(() => {
    return () => {
      if (boundaryMonitor) {
        cancelAnimationFrame(boundaryMonitor);
      }
    };
  }, [boundaryMonitor]);

  // Cleanup shot data when dialog closes
  useEffect(() => {
    if (!open) {
      // Always cleanup when closing, regardless of shotData state
      if (shotData) {
        cleanupShotData();
      }
      setShotData(null);
      setCurrentShotIndex(0);
      setIsPlaying(false);
      setEditMode(false);
    }
  }, [open]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open || editMode) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          navigateShot(-1);
          break;
        case 'ArrowRight':
          event.preventDefault();
          navigateShot(1);
          break;
        case ' ':
          event.preventDefault();
          togglePlayback();
          break;
        case 'e':
          event.preventDefault();
          handleEditShot();
          break;
        case 'Escape':
          event.preventDefault();
          if (editMode) {
            handleCancelEdit();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, editMode, currentShotIndex, shotData]);

  const extractVideoIdFromUrl = (url: string): string | null => {
    try {
      // Extract UUID from S3 URL - look for pattern like "full_video_[UUID].mp4" 
      const match = url.match(/full_video_([a-f0-9\-]{36})\.mp4/i);
      if (match) {
        return match[1];
      }
      
      // Also try to extract from other URL patterns that might contain UUID
      const uuidMatch = url.match(/([a-f0-9\-]{36})/i);
      if (uuidMatch) {
        return uuidMatch[1];
      }
      
      return null;
    } catch (error) {
      console.warn('[VideoFramingEditor] Error extracting videoId from URL:', error);
      return null;
    }
  };

  const loadExistingShots = async (videoId: string) => {
    try {
      console.log('[VideoFramingEditor] Checking for existing shot data...', { videoId });
      
      const response = await fetch(`${SERVER_BASE_URL}/api/shots/${videoId}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[VideoFramingEditor] Found existing shot data:', data);
        return data;
      } else if (response.status === 404) {
        console.log('[VideoFramingEditor] No existing shot data found');
        return null;
      } else {
        throw new Error(`Failed to load existing shots: ${response.status}`);
      }
    } catch (error) {
      console.warn('[VideoFramingEditor] Error loading existing shots:', error);
      return null;
    }
  };

  const detectShots = async () => {
    setLoading(true);
    setError(null);

    try {
      // First, try to load existing shot data
      const videoId = extractVideoIdFromUrl(videoUrl);
      if (videoId) {
        const existingData = await loadExistingShots(videoId);
        if (existingData) {
          console.log('[VideoFramingEditor] Using existing shot data with saved edits');
          setShotData(existingData);
          setLoading(false);
          return;
        }
      }

      console.log('[VideoFramingEditor] Starting fresh shot detection...', { videoUrl, inPoint, duration });

      const response = await fetch(`${SERVER_BASE_URL}/api/detect-shots`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          videoUrl,
          inPoint,
          duration
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to detect shots');
      }

      const data = await response.json();
      console.log('[VideoFramingEditor] Fresh shot detection complete:', data);
      console.log('[VideoFramingEditor] First shot structure:', data.shots[0]);

      // Transform the API response to match our ShotData interface
      const shotData: ShotData = {
        videoId: data.videoId,
        originalVideoUrl: videoUrl,
        inPoint,
        duration,
        editStart: data.editStart,
        editEnd: data.editEnd,
        editDuration: data.editDuration,
        fullVideoDuration: data.fullVideoDuration,
        fullVideoPath: videoUrl,
        frameRate: data.frameRate || 30,
        shots: data.shots,
        createdAt: new Date().toISOString()
      };

      setShotData(shotData);
      setCurrentShotIndex(0);

    } catch (error) {
      console.error('[VideoFramingEditor] Shot detection error:', error);
      setError(error instanceof Error ? error.message : 'Failed to detect shots');
    } finally {
      setLoading(false);
    }
  };

  const forceRefreshShots = async () => {
    console.log('[VideoFramingEditor] Force refreshing shots - bypassing existing data');
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${SERVER_BASE_URL}/api/detect-shots`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          videoUrl,
          inPoint,
          duration,
          force: true  // Force fresh detection, bypass cache
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to detect shots');
      }

      const data = await response.json();
      console.log('[VideoFramingEditor] Force refresh shot detection complete:', data);
      
      // Transform the API response to match our ShotData interface
      const shotData: ShotData = {
        videoId: data.videoId,
        originalVideoUrl: data.originalVideoUrl,
        inPoint: data.inPoint || 0,
        duration: data.duration,
        editStart: data.editStart,
        editEnd: data.editEnd,
        editDuration: data.editDuration,
        fullVideoDuration: data.fullVideoDuration,
        fullVideoPath: data.fullVideoPath,
        shots: data.shots.map((shot: any) => ({
          ...shot,
          originalFraming: shot.originalFraming || null,
          customFraming: shot.customFraming || null
        }))
      };

      setShotData(shotData);
      setCurrentShotIndex(0);

    } catch (error) {
      console.error('[VideoFramingEditor] Force refresh error:', error);
      setError(error instanceof Error ? error.message : 'Failed to refresh shots');
    } finally {
      setLoading(false);
    }
  };

  const cleanupShotData = async () => {
    if (!shotData) return;

    try {
      // Cleanup temp shot MP4s
      const cleanupResponse = await fetch(`${SERVER_BASE_URL}/api/cleanup-shot-mp4s`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videoId: shotData.videoId
        }),
      });
      
      if (cleanupResponse.ok) {
        const result = await cleanupResponse.json();
        console.log('[VideoFramingEditor] Temp files cleaned up:', result.message);
      }
      
      // Also cleanup shot data (existing functionality)
      await fetch(`${SERVER_BASE_URL}/api/shots/${shotData.videoId}`, {
        method: 'DELETE'
      });
      console.log('[VideoFramingEditor] Shot data cleaned up');
    } catch (error) {
      console.error('[VideoFramingEditor] Cleanup error:', error);
    }
  };

  // Get current shot video URL (individual MP4 for frame-accurate playback)
  const getCurrentShotVideoUrl = () => {
    if (!shotData) return '';
    
    const currentShot = shotData.shots[currentShotIndex];
    if (!currentShot) return '';
    
    // In edit mode, show the horizontal 16:9 video for framing adjustment
    if (editMode && horizontalUrl) {
      console.log('[VideoFramingEditor] Using horizontal video for editing:', {
        shotId: currentShot.id,
        horizontalUrl: horizontalUrl
      });
      return horizontalUrl;
    }
    
    // Use individual shot MP4 for frame-accurate playback in preview mode
    const shotMP4Url = `${SERVER_BASE_URL}/uploads/temp/shot_${shotData.videoId}_${currentShot.id}_temp.mp4`;
    
    console.log('[VideoFramingEditor] Using shot MP4:', {
      shotId: currentShot.id,
      shotMP4Url: shotMP4Url
    });
    
    return shotMP4Url;
  };

  const navigateShot = (direction: number) => {
    if (!shotData) return;

    // Stop any ongoing boundary monitoring
    if (boundaryMonitor) {
      cancelAnimationFrame(boundaryMonitor);
      setBoundaryMonitor(null);
    }
    
    // Pause video if playing
    if (videoRef.current && isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    }

    const newIndex = Math.max(0, Math.min(shotData.shots.length - 1, currentShotIndex + direction));
    setCurrentShotIndex(newIndex);
    
    // Video source will automatically update via getCurrentShotVideoUrl()
    // Each shot MP4 starts at time 0, so no seeking needed
    console.log('[VideoFramingEditor] Navigated to shot:', {
      shotId: shotData.shots[newIndex].id,
      shotIndex: newIndex
    });
  };

  const togglePlayback = () => {
    if (!videoRef.current || !shotData) return;
    
    // Prevent concurrent toggle calls
    if (isTogglingPlayback.current) {
      console.log('[VideoFramingEditor] togglePlayback already in progress, ignoring');
      return;
    }
    
    isTogglingPlayback.current = true;
    console.log('[VideoFramingEditor] togglePlayback called, isPlaying:', isPlaying);

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
      if (boundaryMonitor) {
        cancelAnimationFrame(boundaryMonitor);
        setBoundaryMonitor(null);
      }
      isTogglingPlayback.current = false;
    } else {
      const currentShot = shotData.shots[currentShotIndex];
      
      // In edit mode with horizontal video, ensure we start at shot start time
      if (editMode && horizontalUrl) {
        videoRef.current.currentTime = currentShot.startTime;
        console.log('[VideoFramingEditor] Starting playback in edit mode from shot start:', {
          shotId: currentShot.id,
          startTime: currentShot.startTime
        });
      }
      
      // Use promise to handle play operation properly
      videoRef.current.play().then(() => {
        console.log('[VideoFramingEditor] Playback started successfully');
        setIsPlaying(true);
        isTogglingPlayback.current = false;
        // Start boundary monitoring to auto-stop at shot end
        const monitor = requestAnimationFrame(monitorShotBoundary);
        setBoundaryMonitor(monitor);
        console.log('[VideoFramingEditor] Started boundary monitoring');
      }).catch((error) => {
        console.warn('[VideoFramingEditor] Play was interrupted:', error);
        setIsPlaying(false);
        isTogglingPlayback.current = false;
      });
    }
  };

  // Monitor shot boundaries - handles both individual shot MP4s and horizontal video in edit mode
  const monitorShotBoundary = useCallback(() => {
    if (!videoRef.current || !shotData) {
      if (boundaryMonitor) {
        cancelAnimationFrame(boundaryMonitor);
        setBoundaryMonitor(null);
      }
      return;
    }
    
    // Only monitor if video is actually playing
    if (videoRef.current.paused) {
      return;
    }
    
    const currentShot = shotData.shots[currentShotIndex];
    
    if (editMode && horizontalUrl) {
      // In edit mode with horizontal video, monitor shot end time
      if (videoRef.current.currentTime >= currentShot.endTime) {
        console.log('[VideoFramingEditor] Shot ended in edit mode, returning to start:', {
          shotId: currentShot.id,
          currentTime: videoRef.current.currentTime,
          shotEndTime: currentShot.endTime
        });
        
        videoRef.current.pause();
        setIsPlaying(false);
        videoRef.current.currentTime = currentShot.startTime; // Return to shot start
        
        if (boundaryMonitor) {
          cancelAnimationFrame(boundaryMonitor);
          setBoundaryMonitor(null);
        }
        return;
      }
    } else {
      // For individual shot MP4s, simply check if we've reached the end
      if (videoRef.current.currentTime >= videoRef.current.duration - 0.1) { // Small buffer
        console.log('[VideoFramingEditor] Shot MP4 ended, pausing:', {
          shotId: currentShot.id,
          currentTime: videoRef.current.currentTime,
          duration: videoRef.current.duration
        });
        
        videoRef.current.pause();
        setIsPlaying(false);
        videoRef.current.currentTime = 0; // Reset to start for easy replay
        
        if (boundaryMonitor) {
          cancelAnimationFrame(boundaryMonitor);
          setBoundaryMonitor(null);
        }
        return;
      }
    }
    
    // Continue monitoring
    const monitor = requestAnimationFrame(monitorShotBoundary);
    setBoundaryMonitor(monitor);
  }, [currentShotIndex, shotData, editMode, horizontalUrl, boundaryMonitor]);


  const handleEditShot = () => {
    if (!shotData || !videoRef.current) return;

    const currentShot = shotData.shots[currentShotIndex];
    
    // Set up crop box with current framing or default
    const framing = currentShot.customFraming || { x: 656, y: 0, width: 608, height: 1088 };
    setCropBox(framing);
    setOriginalCropBox(framing);
    setEditMode(true);
    
    // When entering edit mode with horizontal video, seek to shot start time
    if (horizontalUrl) {
      // Wait for video source to change, then seek to shot start time
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.currentTime = currentShot.startTime;
          console.log('[VideoFramingEditor] Entered edit mode, seeking to shot start:', {
            shotId: currentShot.id,
            startTime: currentShot.startTime,
            hasCustomFraming: !!currentShot.customFraming
          });
        }
      }, 100);
    }
  };

  const handleSaveEdit = async () => {
    if (!shotData) return;

    const currentShot = shotData.shots[currentShotIndex];
    console.log('[VideoFramingEditor] Saving shot framing for shot', currentShot.id);

    // Show loading state
    setSaving(true);
    setError(null);

    const maxRetries = 3;
    const timeoutMs = 30000; // 30 second timeout

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        const response = await fetch(`${SERVER_BASE_URL}/api/update-shot-framing`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            videoId: shotData.videoId,
            shotId: currentShot.id,
            customFraming: cropBox
          }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Server error: ${response.status}`);
        }

        const result = await response.json();
        
        // Update local shot data
        const updatedShots = shotData.shots.map(shot => 
          shot.id === currentShot.id ? { ...shot, ...result.shot } : shot
        );
        
        setShotData({ ...shotData, shots: updatedShots });
        setEditMode(false);
        setSaving(false);

        console.log('[VideoFramingEditor] Successfully saved shot framing for shot', currentShot.id);
        
        // When exiting edit mode, we switch back to shot MP4 which starts at time 0
        // Wait for video source to change, then reset to start
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.currentTime = 0;
            console.log('[VideoFramingEditor] Saved edit mode, reset to shot MP4 start');
          }
        }, 100);
        
        // Success feedback
        setError(null);
        // Could add success toast here if desired
        return; // Success - exit retry loop

      } catch (error) {
        const isNetworkError = error instanceof Error && 
          (error.name === 'AbortError' || error.message.includes('fetch'));
        
        console.error(`[VideoFramingEditor] Save attempt ${attempt}/${maxRetries} failed:`, error);

        if (attempt < maxRetries && isNetworkError) {
          // Wait before retry (exponential backoff)
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          console.log(`[VideoFramingEditor] Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }

        // Final failure
        setSaving(false);
        const errorMessage = error instanceof Error ? error.message : 'Failed to save framing';
        
        if (error instanceof Error && error.name === 'AbortError') {
          setError('Save timed out. Please try again.');
        } else if (isNetworkError) {
          setError('Network error. Please check connection and try again.');
        } else {
          setError(errorMessage);
        }
        
        console.error('[VideoFramingEditor] Final save failure after all retries:', error);
        break;
      }
    }
  };

  const handleCancelEdit = () => {
    setCropBox(originalCropBox);
    setEditMode(false);
    
    // When exiting edit mode, we switch back to shot MP4 which starts at time 0
    // Wait for video source to change, then reset to start
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        console.log('[VideoFramingEditor] Cancelled edit mode, reset to shot MP4 start');
      }
    }, 100);
  };

  const handleToggleBlurredBackground = async (checked: boolean) => {
    if (!shotData) return;
    
    const currentShot = shotData.shots[currentShotIndex];
    console.log('[VideoFramingEditor] Toggling blurred background for shot', currentShot.id, 'to', checked);
    
    try {
      const response = await fetch(`${SERVER_BASE_URL}/api/update-shot-framing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videoId: shotData.videoId,
          shotId: currentShot.id,
          customFraming: currentShot.customFraming, // Can be null for blurred background
          useBlurredBackground: checked
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Update local shot data
      const updatedShots = shotData.shots.map(shot => 
        shot.id === currentShot.id ? { ...shot, useBlurredBackground: checked } : shot
      );
      
      setShotData({ ...shotData, shots: updatedShots });
      
      console.log('[VideoFramingEditor] Successfully updated blurred background setting for shot', currentShot.id);
      
    } catch (error) {
      console.error('[VideoFramingEditor] Error updating blurred background:', error);
      setError(error instanceof Error ? error.message : 'Failed to update blurred background setting');
    }
  };


  const handleReassemble = async () => {
    if (!shotData) return;

    setReassembling(true);
    setError(null);
    
    // Reset progress state
    setProgressStep('');
    setProgressPercent(0);
    setProgressMessage('Starting video generation...');

    try {
      const outputFileName = `reframed_${Date.now()}.mp4`;

      const response = await fetch(`${SERVER_BASE_URL}/api/reassemble-video`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          videoId: shotData.videoId,
          outputFileName
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reassemble video');
      }

      const result = await response.json();
      console.log('[VideoFramingEditor] Video reassembly complete:', result);

      if (onVideoGenerated && result.s3Url) {
        onVideoGenerated(result.s3Url);
      }

      // Show success message with backup information
      let message = `Video successfully generated!\nEdited shots: ${result.editedShots}/${result.totalShots}`;
      if (result.backupUrl) {
        message += `\n\nOriginal video backed up and new version uploaded.`;
        message += `\nBackup URL: ${result.backupUrl}`;
      }
      alert(message);

    } catch (error) {
      console.error('[VideoFramingEditor] Reassembly error:', error);
      setError(error instanceof Error ? error.message : 'Failed to reassemble video');
    } finally {
      setReassembling(false);
      // Clear progress state
      setProgressStep('');
      setProgressPercent(0);
      setProgressMessage('');
    }
  };

  // Mouse event handlers for crop box
  const handleMouseDown = (event: React.MouseEvent, action: 'move' | 'resize') => {
    if (!editMode) return;

    event.preventDefault();
    isDragging.current = true;
    dragElement.current = action;
    dragStart.current = { x: event.clientX, y: event.clientY };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (event: MouseEvent) => {
    if (!isDragging.current || !cropContainerRef.current) return;

    const rect = cropContainerRef.current.getBoundingClientRect();
    const scaleX = 1920 / rect.width;
    const scaleY = 1088 / rect.height;

    const deltaX = (event.clientX - dragStart.current.x) * scaleX;
    const deltaY = (event.clientY - dragStart.current.y) * scaleY;

    if (dragElement.current === 'move') {
      setCropBox(prev => ({
        ...prev,
        x: Math.max(0, Math.min(1920 - prev.width, prev.x + deltaX)),
        y: Math.max(0, Math.min(1088 - prev.height, prev.y + deltaY))
      }));
    } else if (dragElement.current === 'resize') {
      setCropBox(prev => {
        // For 9:16 aspect ratio on 1920x1080 video
        // Max height is 1088, so max width is 1088 * 9/16 = 608
        const minWidth = 304; // Minimum reasonable size (half of max)
        const maxWidth = 608; // Maximum width for 9:16 in 1920x1080
        const newWidth = Math.max(minWidth, Math.min(maxWidth, Math.min(1920 - prev.x, prev.width + deltaX)));
        const newHeight = Math.min(1080, Math.round((newWidth * 16) / 9)); // Maintain 9:16 aspect ratio, max 1080
        
        return {
          ...prev,
          width: newWidth,
          height: Math.min(1088 - prev.y, newHeight)
        };
      });
    }

    dragStart.current = { x: event.clientX, y: event.clientY };
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    dragElement.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  const currentShot = shotData?.shots[currentShotIndex];
  const editedShotsCount = shotData?.shots.filter(shot => shot.isEdited).length || 0;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      PaperProps={{
        sx: { height: '90vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Video Frame Editor</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            {shotData && (
              <Chip 
                label={`${editedShotsCount} edited`} 
                color={editedShotsCount > 0 ? "primary" : "default"}
                size="small"
              />
            )}
            <Button onClick={onClose} size="small">Close</Button>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2, height: '100%', overflow: 'hidden' }}>
        {loading && (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="400px">
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>Detecting shots...</Typography>
            <Typography variant="body2" color="text.secondary">
              This may take a few moments
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            <Button onClick={detectShots} startIcon={<RefreshIcon />} sx={{ ml: 2 }}>
              Retry
            </Button>
          </Alert>
        )}

        {shotData && !loading && (
          <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
            {/* Left side - Filmstrip */}
            <Box sx={{ width: '33%', height: '100%', overflow: 'hidden' }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Shots ({shotData.shots.length})
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(3, 1fr)', 
                    gap: 1,
                    maxHeight: '100%',
                    overflowY: 'auto'
                  }}>
                    {shotData.shots.map((shot, index) => (
                      <Card
                        key={shot.id}
                        sx={{
                          cursor: 'pointer',
                          border: index === currentShotIndex ? '2px solid' : '1px solid',
                          borderColor: index === currentShotIndex ? 'primary.main' : 'divider',
                          position: 'relative',
                          aspectRatio: '9/16'
                        }}
                        onClick={async () => {
                          // Stop any ongoing boundary monitoring
                          if (boundaryMonitor) {
                            cancelAnimationFrame(boundaryMonitor);
                            setBoundaryMonitor(null);
                          }
                          
                          // Pause video if playing and wait for pause to complete
                          if (videoRef.current && isPlaying) {
                            videoRef.current.pause();
                            setIsPlaying(false);
                            // Wait a frame to ensure pause has taken effect
                            await new Promise(resolve => requestAnimationFrame(resolve));
                          }
                          
                          setCurrentShotIndex(index);
                          // Video source will automatically update via getCurrentShotVideoUrl()
                          // Each shot MP4 starts at time 0, so no seeking needed
                          console.log('[VideoFramingEditor] Thumbnail clicked - switched to shot:', {
                            shotId: shot.id,
                            shotIndex: index
                          });
                        }}
                      >
                        <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
                          {shot.thumbnailUrl && (
                            <img
                              src={`${SERVER_BASE_URL}${shot.thumbnailUrl}`}
                              alt={`Shot ${shot.id}`}
                              style={{
                                width: '100%',
                                flexGrow: 1,
                                objectFit: 'cover'
                              }}
                            />
                          )}
                          
                          {shot.isEdited && (
                            <Chip
                              label="Edited"
                              size="small"
                              color="primary"
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                fontSize: '0.6rem',
                                height: '18px'
                              }}
                            />
                          )}
                          
                          <Box sx={{ 
                            position: 'absolute', 
                            bottom: 0, 
                            left: 0, 
                            right: 0, 
                            p: 0.5, 
                            background: 'rgba(0,0,0,0.6)', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center' 
                          }}>
                            <Typography variant="caption" display="block" sx={{ fontSize: '0.65rem', color: 'white' }}>
                              Shot {shot.id}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.8)' }}>
                              {shot.duration.toFixed(1)}s
                            </Typography>
                          </Box>
                        </Box>
                      </Card>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Right side - Main preview */}
            <Box sx={{ width: '67%', height: '100%', overflow: 'hidden' }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Shot controls */}
                  <Box display="flex" alignItems="center" justifyContent="between" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <IconButton onClick={() => navigateShot(-1)} disabled={currentShotIndex === 0}>
                        <ArrowBackIcon />
                      </IconButton>
                      <Typography variant="h6">
                        Shot {currentShot?.id} of {shotData.shots.length}
                      </Typography>
                      <IconButton onClick={() => navigateShot(1)} disabled={currentShotIndex === shotData.shots.length - 1}>
                        <ArrowForwardIcon />
                      </IconButton>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                      {editMode ? (
                        <>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={currentShot?.useBlurredBackground || false}
                                onChange={(e) => handleToggleBlurredBackground(e.target.checked)}
                                size="small"
                              />
                            }
                            label="16:9 with Blur"
                            sx={{ mr: 2 }}
                          />
                          <Button 
                            startIcon={<SaveIcon />} 
                            onClick={handleSaveEdit}
                            variant="contained"
                            size="small"
                            disabled={saving}
                          >
                            {saving ? 'Saving...' : 'Save'}
                          </Button>
                          <Button 
                            startIcon={<CancelIcon />} 
                            onClick={handleCancelEdit}
                            size="small"
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Button 
                          startIcon={<EditIcon />} 
                          onClick={handleEditShot}
                          variant="outlined"
                          size="small"
                        >
                          Edit Framing
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {/* Video preview area */}
                  <Box 
                    ref={cropContainerRef}
                    sx={{ 
                      position: 'relative', 
                      width: '100%', 
                      aspectRatio: '16/9',
                      bgcolor: 'black',
                      overflow: 'hidden'
                    }}
                  >
                    <video
                      ref={videoRef}
                      src={getCurrentShotVideoUrl()}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />

                    {/* Crop overlay in edit mode (only if not using blurred background) */}
                    {editMode && !currentShot?.useBlurredBackground && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          pointerEvents: 'none'
                        }}
                      >
                        {/* Dark overlay */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            bgcolor: 'rgba(0,0,0,0.5)'
                          }}
                        />
                        
                        {/* Crop box */}
                        <Box
                          sx={{
                            position: 'absolute',
                            left: `${(cropBox.x / 1920) * 100}%`,
                            top: `${(cropBox.y / 1080) * 100}%`,
                            width: `${(cropBox.width / 1920) * 100}%`,
                            height: `${(cropBox.height / 1088) * 100}%`,
                            border: '2px solid #fff',
                            cursor: 'move',
                            pointerEvents: 'auto'
                          }}
                          onMouseDown={(e) => handleMouseDown(e, 'move')}
                        >
                          {/* Resize handle */}
                          <Box
                            sx={{
                              position: 'absolute',
                              right: -4,
                              bottom: -4,
                              width: 8,
                              height: 8,
                              bgcolor: '#fff',
                              cursor: 'se-resize'
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              handleMouseDown(e, 'resize');
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {/* Playback controls */}
                  <Box display="flex" alignItems="center" justifyContent="center" mt={2} gap={2}>
                    <IconButton onClick={togglePlayback}>
                      {isPlaying ? <PauseIcon /> : <PlayIcon />}
                    </IconButton>
                    
                    <Typography variant="body2" color="text.secondary">
                      {currentShot && `${currentShot.duration.toFixed(1)}s duration`}
                    </Typography>
                  </Box>

                  {editMode && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        {currentShot?.useBlurredBackground ? (
                          <><strong>16:9 with Blur Mode:</strong> Full horizontal video will be used with blurred pillarboxes. No cropping needed.</>
                        ) : (
                          <><strong>Edit Mode:</strong> Drag the white box to reposition, or drag the corner to resize. The crop will maintain a 9:16 aspect ratio for vertical video.</>
                        )}
                      </Typography>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Progress Section */}
      {reassembling && (
        <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="primary">
              {progressMessage || 'Processing video...'}
            </Typography>
            {progressPercent > 0 && (
              <Typography variant="body2" color="text.secondary">
                {progressPercent.toFixed(1)}%
              </Typography>
            )}
          </Box>
          <LinearProgress 
            variant={progressPercent > 0 ? "determinate" : "indeterminate"} 
            value={progressPercent} 
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )}

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          Use arrow keys to navigate • Space to play/pause • E to edit • Esc to cancel
        </Typography>
        
        <Button onClick={onClose}>Cancel</Button>
        
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => forceRefreshShots()}
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh Shots'}
        </Button>
        
        <Button
          variant="contained"
          startIcon={reassembling ? <CircularProgress size={16} /> : <VideoIcon />}
          onClick={handleReassemble}
          disabled={!shotData || reassembling}
        >
          {reassembling 
            ? (progressMessage ? `${progressMessage}` : 'Generating...') 
            : 'Generate Video'
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VideoFramingEditor;